import React, { useState, useEffect } from "react"
import {
  FiInfo, FiCalendar, FiUser, FiCheckCircle, FiClock,
  FiAlertCircle, FiX, FiCheck, FiSend, FiFile, FiDownload, FiUpload, FiEye, FiRefreshCw,
} from "react-icons/fi"
import { FaMoneyBillWave, FaBan } from "react-icons/fa"
import { LuActivity } from "react-icons/lu"
import { useParams, useNavigate } from "react-router-dom"
import axiosInstance from "../../../libs/axios"
import { uploadFilesWithPresignedPayload } from "../../../libs/presigned-upload"
import { useAuth } from "../../../provider/auth.provider"
import EntityLogs from "../../../utils/logs.utils"
import { formatDate } from "../../activities/edit"
import toast from "react-hot-toast"

// ─── Types ────────────────────────────────────────────────────────────────────

interface SPDocument {
  id: number
  category: "request" | "approval" | "transfer" | "completion"
  url: string
  name: string
  size?: number | null
  mime_type?: string | null
  uploaded_by?: number | null
  uploader?: { id: number; first_name: string; last_name: string } | null
  created_at: string
}

interface SPEmployee {
  id: number
  first_name: string
  last_name: string
  employee_code?: string | null
  association_type?: string
  designation?: { id: number; name: string } | null
  department?: { id: number; name: string } | null
}

interface SPRequest {
  id: number
  employee_id: number
  supplier_name: string
  reason_for_request: string
  other_remarks?: string | null
  request_description?: string | null
  total_payable_amount: number
  bank_name?: string | null
  ifsc_code?: string | null
  account_number?: string | null
  upi_id?: string | null
  qr_code?: string | null
  status: string
  approved_by?: number | null
  approved_at?: string | null
  approved_amount?: number | null
  approver_remarks?: string | null
  rejected_by?: number | null
  rejected_at?: string | null
  rejection_remarks?: string | null
  transferred_by?: number | null
  transferred_at?: string | null
  transferred_amount?: number | null
  transfer_remarks?: string | null
  transfer_date?: string | null
  completed_by?: number | null
  completed_at?: string | null
  completion_remarks?: string | null
  cancelled_by?: number | null
  cancelled_at?: string | null
  cancellation_remarks?: string | null
  created_at: string
  employee?: SPEmployee | null
  approver?: SPEmployee | null
  rejector?: SPEmployee | null
  transferrer?: SPEmployee | null
  completer?: SPEmployee | null
  canceller?: SPEmployee | null
  documents?: SPDocument[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (amount?: number | null) => {
  if (amount === undefined || amount === null) return "—"
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(amount)
}

const fullName = (e?: SPEmployee | null) =>
  e ? `${e.first_name} ${e.last_name}` : "—"

const reasonLabels: Record<string, string> = {
  purchase: "Purchase",
  repair: "Repair",
  office_expenses: "Office Expenses",
  other: "Other",
}

const statusConfig: Record<string, { label: string; cls: string }> = {
  requested: { label: "Requested", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  payment: { label: "Payment", cls: "bg-yellow-100 text-yellow-700" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-600" },
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = statusConfig[status] || { label: status, cls: "bg-gray-100 text-gray-600" }
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  )
}

const docCategoryLabel: Record<string, string> = {
  request: "Request Documents",
  approval: "Approval Documents",
  transfer: "Transfer Documents",
  completion: "Completion Documents",
}

// ─── Drawer component ─────────────────────────────────────────────────────────

interface DrawerProps {
  open: boolean
  title: string
  subtitle?: string
  onClose: () => void
  disabled?: boolean
  children: React.ReactNode
  footer: React.ReactNode
}

const Drawer: React.FC<DrawerProps> = ({ open, title, subtitle, onClose, disabled, children, footer }) => {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={disabled ? undefined : onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b bg-gray-50">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} disabled={disabled} className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50">
            <FiX size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
        <div className="border-t bg-gray-50 px-5 py-4 flex justify-end gap-3">{footer}</div>
      </div>
    </>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const SupplierPaymentView: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission, user } = useAuth()

  const canApprove = hasPermission("supplier_payment", "approve")
  const canTransfer = hasPermission("supplier_payment", "transfer")
  const canManage = hasPermission("supplier_payment", "manage_restrictions")
  const canViewPaymentInfo = hasPermission("supplier_payment", "view_payment_info")

  const [request, setRequest] = useState<SPRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("details")

  // Approve drawer
  const [showApprove, setShowApprove] = useState(false)
  const [approvedAmount, setApprovedAmount] = useState("")
  const [approverRemarks, setApproverRemarks] = useState("")

  // Reject drawer
  const [showReject, setShowReject] = useState(false)
  const [rejectionRemarks, setRejectionRemarks] = useState("")

  // Transfer drawer
  const [showTransfer, setShowTransfer] = useState(false)
  const [transferAmount, setTransferAmount] = useState("")
  const [transferRemarks, setTransferRemarks] = useState("")
  const [transferDate, setTransferDate] = useState("")

  // Complete drawer
  const [showComplete, setShowComplete] = useState(false)
  const [completionRemarks, setCompletionRemarks] = useState("")

  // Cancel drawer
  const [showCancel, setShowCancel] = useState(false)
  const [cancellationRemarks, setCancellationRemarks] = useState("")

  // Document uploads per drawer
  const [approveFiles, setApproveFiles] = useState<File[]>([])
  const [transferFiles, setTransferFiles] = useState<File[]>([])
  const [completeFiles, setCompleteFiles] = useState<File[]>([])

  useEffect(() => { fetchRequest() }, [id])

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !actionLoading) {
        setShowApprove(false); setShowReject(false)
        setShowTransfer(false); setShowComplete(false); setShowCancel(false)
      }
    }
    document.addEventListener("keydown", esc)
    return () => document.removeEventListener("keydown", esc)
  }, [actionLoading])

  const fetchRequest = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(`/supplier-payment/${id}`)
      const data: SPRequest = res.data.data.request
      setRequest(data)
      setApprovedAmount(String(data.total_payable_amount))
      setTransferAmount(String(data.approved_amount ?? data.total_payable_amount))
    } catch {
      toast.error("Failed to load request")
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!approvedAmount || Number(approvedAmount) <= 0) { toast.error("Enter a valid approved amount"); return }
    try {
      setActionLoading(true)
      let docs: any[] = []
      if (approveFiles.length > 0) {
        const uploaded = await uploadFilesWithPresignedPayload(approveFiles, { folderName: "supplier-payment" })
        docs = uploaded.map(f => ({ name: f.fileName, url: f.fileUrl, size: f.fileSize, mime_type: f.mimeType }))
      }
      await axiosInstance.put("/supplier-payment/approve-reject", {
        id: Number(id), status: "approved",
        approved_amount: Number(approvedAmount),
        approver_remarks: approverRemarks.trim() || undefined,
        documents: docs,
      })
      toast.success("Request approved")
      setShowApprove(false)
      setApproveFiles([])
      await fetchRequest()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to approve")
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!rejectionRemarks.trim()) { toast.error("Rejection remarks are required"); return }
    try {
      setActionLoading(true)
      await axiosInstance.put("/supplier-payment/approve-reject", {
        id: Number(id), status: "rejected",
        rejection_remarks: rejectionRemarks.trim(),
      })
      toast.success("Request rejected")
      setShowReject(false)
      setRejectionRemarks("")
      await fetchRequest()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to reject")
    } finally {
      setActionLoading(false)
    }
  }

  const handleTransfer = async () => {
    if (!transferDate) { toast.error("Transfer date is required"); return }
    try {
      setActionLoading(true)
      let docs: any[] = []
      if (transferFiles.length > 0) {
        const uploaded = await uploadFilesWithPresignedPayload(transferFiles, { folderName: "supplier-payment" })
        docs = uploaded.map(f => ({ name: f.fileName, url: f.fileUrl, size: f.fileSize, mime_type: f.mimeType }))
      }
      await axiosInstance.put("/supplier-payment/transfer", {
        id: Number(id),
        transferred_amount: transferAmount ? Number(transferAmount) : undefined,
        transfer_remarks: transferRemarks.trim() || undefined,
        transfer_date: transferDate,
        documents: docs,
      })
      toast.success("Payment transferred")
      setShowTransfer(false)
      setTransferFiles([])
      await fetchRequest()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to transfer")
    } finally {
      setActionLoading(false)
    }
  }

  const handleComplete = async () => {
    try {
      setActionLoading(true)
      let docs: any[] = []
      if (completeFiles.length > 0) {
        const uploaded = await uploadFilesWithPresignedPayload(completeFiles, { folderName: "supplier-payment" })
        docs = uploaded.map(f => ({ name: f.fileName, url: f.fileUrl, size: f.fileSize, mime_type: f.mimeType }))
      }
      await axiosInstance.put("/supplier-payment/complete", {
        id: Number(id),
        completion_remarks: completionRemarks.trim() || undefined,
        documents: docs,
      })
      toast.success("Request completed")
      setShowComplete(false)
      setCompleteFiles([])
      await fetchRequest()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to complete")
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancel = async () => {
    try {
      setActionLoading(true)
      await axiosInstance.put("/supplier-payment/cancel", {
        id: Number(id),
        cancellation_remarks: cancellationRemarks.trim() || undefined,
      })
      toast.success("Request cancelled")
      setShowCancel(false)
      await fetchRequest()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to cancel")
    } finally {
      setActionLoading(false)
    }
  }

  // ─── Tabs ──────────────────────────────────────────────────────────────────

  const tabs = [
    { id: "details", label: "Payment Request", icon: FiInfo },
    ...(canViewPaymentInfo ? [{ id: "payment_info", label: "Payment Info", icon: FaMoneyBillWave }] : []),
    { id: "documents", label: "Documents", icon: FiFile },
    { id: "workflow", label: "Workflow", icon: FiSend },
    { id: "logs", label: "Logs", icon: LuActivity },
  ]

  // ─── Detail Section ───────────────────────────────────────────────────────

  const renderDetails = () => (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded p-5">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Request Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Employee">{fullName(request?.employee)}</Field>
          <Field label="Employee Code">{request?.employee?.employee_code || "—"}</Field>
          <Field label="Designation">{request?.employee?.designation?.name || "—"}</Field>
          <Field label="Department">{request?.employee?.department?.name || "—"}</Field>
          <Field label="Supplier Name"><span className="font-semibold">{request?.supplier_name}</span></Field>
          <Field label="Reason">{reasonLabels[request?.reason_for_request || ""] || request?.reason_for_request || "—"}</Field>
          <Field label="Requested Amount"><span className="font-bold text-blue-800">{fmt(request?.total_payable_amount)}</span></Field>
          <Field label="Status"><StatusBadge status={request?.status || ""} /></Field>
          <Field label="Requested On">{request?.created_at ? formatDate(request.created_at) : "—"}</Field>
          {request?.other_remarks && (
            <div className="md:col-span-2">
              <Field label="Other Remarks">{request.other_remarks}</Field>
            </div>
          )}
          {request?.request_description && (
            <div className="md:col-span-2">
              <Field label="Description">
                <p className="bg-gray-50 p-3 rounded text-sm">{request.request_description}</p>
              </Field>
            </div>
          )}
        </div>
      </div>

      {(request?.status === "approved" || request?.status === "payment" || request?.status === "completed") && request?.approver && (
        <div className="bg-white border border-gray-200 rounded p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Approval Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Approved By">{fullName(request.approver)}</Field>
            <Field label="Approved At">{request.approved_at ? formatDate(request.approved_at) : "—"}</Field>
            <Field label="Approved Amount"><span className="font-bold text-green-800">{fmt(request.approved_amount)}</span></Field>
            {request.approver_remarks && (
              <div className="md:col-span-2">
                <Field label="Approver Remarks">
                  <p className="bg-green-50 p-3 rounded text-sm">{request.approver_remarks}</p>
                </Field>
              </div>
            )}
          </div>
        </div>
      )}

      {request?.status === "payment" || request?.status === "completed" ? (
        request?.transferrer && (
          <div className="bg-white border border-gray-200 rounded p-5">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Transfer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Transferred By">{fullName(request.transferrer)}</Field>
              <Field label="Transfer Date">{request.transfer_date || "—"}</Field>
              <Field label="Transferred At">{request.transferred_at ? formatDate(request.transferred_at) : "—"}</Field>
              <Field label="Transferred Amount"><span className="font-bold text-blue-800">{fmt(request.transferred_amount)}</span></Field>
              {request.transfer_remarks && (
                <div className="md:col-span-2">
                  <Field label="Transfer Remarks">
                    <p className="bg-blue-50 p-3 rounded text-sm">{request.transfer_remarks}</p>
                  </Field>
                </div>
              )}
            </div>
          </div>
        )
      ) : null}

      {request?.status === "completed" && request?.completer && (
        <div className="bg-white border border-gray-200 rounded p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Completion Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Completed By">{fullName(request.completer)}</Field>
            <Field label="Completed At">{request.completed_at ? formatDate(request.completed_at) : "—"}</Field>
            {request.completion_remarks && (
              <div className="md:col-span-2">
                <Field label="Completion Remarks">
                  <p className="bg-emerald-50 p-3 rounded text-sm">{request.completion_remarks}</p>
                </Field>
              </div>
            )}
          </div>
        </div>
      )}

      {request?.status === "rejected" && (
        <div className="bg-white border border-gray-200 rounded p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Rejection Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Rejected By">{fullName(request.rejector)}</Field>
            <Field label="Rejected At">{request.rejected_at ? formatDate(request.rejected_at) : "—"}</Field>
            {request.rejection_remarks && (
              <div className="md:col-span-2">
                <Field label="Rejection Remarks">
                  <p className="bg-red-50 p-3 rounded text-sm text-red-800">{request.rejection_remarks}</p>
                </Field>
              </div>
            )}
          </div>
        </div>
      )}

      {request?.status === "cancelled" && request?.canceller && (
        <div className="bg-white border border-gray-200 rounded p-5">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Cancellation Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Cancelled By">{fullName(request.canceller)}</Field>
            <Field label="Cancelled At">{request.cancelled_at ? formatDate(request.cancelled_at) : "—"}</Field>
            {request.cancellation_remarks && (
              <div className="md:col-span-2">
                <Field label="Cancellation Remarks">
                  <p className="bg-gray-100 p-3 rounded text-sm">{request.cancellation_remarks}</p>
                </Field>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const renderPaymentInfo = () => (
    <div className="bg-white border border-gray-200 rounded p-5">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Supplier Payment Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Bank Name">{request?.bank_name || "—"}</Field>
        <Field label="IFSC Code"><span className="font-mono">{request?.ifsc_code || "—"}</span></Field>
        <Field label="Account Number"><span className="font-mono">{request?.account_number || "—"}</span></Field>
        <Field label="UPI ID">{request?.upi_id || "—"}</Field>
        {request?.qr_code && (
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-500">QR Code</label>
            <div className="mt-2">
              <img src={request.qr_code} alt="Payment QR" className="w-48 h-48 object-contain border border-gray-200 rounded" />
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderDocuments = () => {
    const docs = request?.documents || []
    const categories = ["request", "approval", "transfer", "completion"] as const
    return (
      <div className="space-y-4">
        {categories.map((cat) => {
          const catDocs = docs.filter((d) => d.category === cat)
          if (catDocs.length === 0) return null
          return (
            <div key={cat} className="bg-white border border-gray-200 rounded p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{docCategoryLabel[cat]}</h3>
              <div className="space-y-2">
                {catDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded border border-gray-100">
                    <FiFile className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{doc.name || doc.url?.split("/").pop() || "Document"}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {doc.uploader ? `${doc.uploader.first_name} ${doc.uploader.last_name}` : ""}
                        {doc.uploader ? " · " : ""}{formatDate(doc.created_at)}
                        {doc.size ? ` · ${(doc.size / 1024).toFixed(1)} KB` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <a href={doc.url} target="_blank" rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                        title="View"
                      >
                        <FiEye className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => {
                          const a = document.createElement("a")
                          a.href = doc.url
                          a.download = doc.name || "document"
                          a.target = "_blank"
                          document.body.appendChild(a)
                          a.click()
                          document.body.removeChild(a)
                        }}
                        className="p-1.5 text-gray-400 hover:text-green-600 rounded transition-colors"
                        title="Download"
                      >
                        <FiDownload className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
        {docs.length === 0 && (
          <div className="bg-white border border-gray-200 rounded p-12 text-center text-gray-400">No documents attached</div>
        )}
      </div>
    )
  }

  const renderWorkflow = () => {
    const s = request?.status || "requested"
    const isApproved = ["approved", "payment", "completed"].includes(s)
    const isPayment = ["payment", "completed"].includes(s)
    const isCompleted = s === "completed"
    const isRejected = s === "rejected"
    const isCancelled = s === "cancelled"

    const steps = [
      {
        key: "requested",
        label: "Request Submitted",
        icon: FiSend,
        active: true,
        details: (
          <div className="space-y-1 mt-2">
            <Detail k="Employee">{fullName(request?.employee)}</Detail>
            <Detail k="Supplier">{request?.supplier_name}</Detail>
            <Detail k="Reason">{reasonLabels[request?.reason_for_request || ""] || request?.reason_for_request}</Detail>
            <Detail k="Amount">{fmt(request?.total_payable_amount)}</Detail>
            {request?.request_description && (
              <p className="text-xs italic bg-gray-50 p-2 rounded mt-2">"{request.request_description}"</p>
            )}
          </div>
        ),
        date: request?.created_at ? formatDate(request.created_at) : "—",
      },
      {
        key: "approval",
        label: isRejected ? "Request Rejected" : "Approval",
        icon: isApproved ? FiCheck : isRejected ? FiX : FiClock,
        active: isApproved || isRejected,
        colorClass: isApproved ? "bg-green-600 text-white" : isRejected ? "bg-red-600 text-white" : "bg-gray-200 text-gray-500",
        details: isApproved && request?.approver ? (
          <div className="space-y-1 mt-2">
            <Detail k="Approved By">{fullName(request.approver)}</Detail>
            <Detail k="Approved Amount">{fmt(request.approved_amount)}</Detail>
            {request.approver_remarks && <p className="text-xs italic bg-green-50 p-2 rounded">"{request.approver_remarks}"</p>}
          </div>
        ) : isRejected ? (
          <div className="space-y-1 mt-2">
            <Detail k="Rejected By">{fullName(request?.rejector)}</Detail>
            {request?.rejection_remarks && <p className="text-xs italic bg-red-50 p-2 rounded text-red-700">"{request.rejection_remarks}"</p>}
          </div>
        ) : <p className="text-xs text-gray-400 italic mt-2">Awaiting approval...</p>,
        date: isApproved ? (request?.approved_at ? formatDate(request.approved_at) : "—") :
          isRejected ? (request?.rejected_at ? formatDate(request.rejected_at) : "—") : "Pending",
      },
      {
        key: "payment",
        label: "Payment Transfer",
        icon: isPayment ? FaMoneyBillWave : FiClock,
        active: isPayment,
        colorClass: isPayment ? "bg-yellow-600 text-white" : "bg-gray-200 text-gray-500",
        details: isPayment && request?.transferrer ? (
          <div className="space-y-1 mt-2">
            <Detail k="Transferred By">{fullName(request.transferrer)}</Detail>
            <Detail k="Amount">{fmt(request.transferred_amount)}</Detail>
            <Detail k="Date">{request.transfer_date || "—"}</Detail>
            {request.transfer_remarks && <p className="text-xs italic bg-yellow-50 p-2 rounded">"{request.transfer_remarks}"</p>}
          </div>
        ) : <p className="text-xs text-gray-400 italic mt-2">Awaiting transfer...</p>,
        date: isPayment ? (request?.transferred_at ? formatDate(request.transferred_at) : "—") : "Pending",
      },
      {
        key: "completed",
        label: "Completed",
        icon: isCompleted ? FiCheckCircle : FiClock,
        active: isCompleted,
        colorClass: isCompleted ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-500",
        details: isCompleted && request?.completer ? (
          <div className="space-y-1 mt-2">
            <Detail k="Completed By">{fullName(request.completer)}</Detail>
            {request.completion_remarks && <p className="text-xs italic bg-emerald-50 p-2 rounded">"{request.completion_remarks}"</p>}
          </div>
        ) : <p className="text-xs text-gray-400 italic mt-2">Pending completion...</p>,
        date: isCompleted ? (request?.completed_at ? formatDate(request.completed_at) : "—") : "Pending",
      },
    ]

    const visibleSteps = isRejected || isCancelled ? steps.slice(0, 2) : steps

    return (
      <div className="bg-white border border-gray-200 rounded p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-6">Workflow Timeline</h3>
        <div className="relative">
          <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-gray-200" />
          {visibleSteps.map((step, i) => {
            const Icon = step.icon as any
            return (
              <div key={step.key} className={`relative flex items-start ${i < visibleSteps.length - 1 ? "mb-8" : ""}`}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full z-10 shadow-md ${step.colorClass || (step.active ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500")}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="ml-6 flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">{step.label}</h4>
                    <span className="text-xs text-gray-400">{step.date}</span>
                  </div>
                  {step.details}
                </div>
              </div>
            )
          })}

          {isCancelled && (
            <div className="relative flex items-start">
              <div className="flex items-center justify-center w-8 h-8 rounded-full z-10 shadow-md bg-gray-500 text-white">
                <FaBan className="w-4 h-4" />
              </div>
              <div className="ml-6 flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Cancelled</h4>
                  <span className="text-xs text-gray-400">{request?.cancelled_at ? formatDate(request.cancelled_at) : "—"}</span>
                </div>
                <div className="space-y-1 mt-2">
                  <Detail k="Cancelled By">{fullName(request?.canceller)}</Detail>
                  {request?.cancellation_remarks && <p className="text-xs italic bg-gray-50 p-2 rounded">"{request.cancellation_remarks}"</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`mt-6 p-4 rounded-lg border-2 ${
          isCompleted ? "bg-emerald-50 border-emerald-200" :
          isPayment ? "bg-yellow-50 border-yellow-200" :
          isApproved ? "bg-green-50 border-green-200" :
          isRejected ? "bg-red-50 border-red-200" :
          isCancelled ? "bg-gray-50 border-gray-200" :
          "bg-blue-50 border-blue-200"
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Current Status</p>
              <p className="text-xs text-gray-600 mt-1">
                {isCompleted ? "Request has been completed" :
                 isPayment ? "Payment has been transferred, awaiting completion" :
                 isApproved ? "Request is approved, awaiting payment transfer" :
                 isRejected ? "Request has been rejected" :
                 isCancelled ? "Request has been cancelled" :
                 "Request is pending approval"}
              </p>
            </div>
            <StatusBadge status={request?.status || "requested"} />
          </div>
        </div>
      </div>
    )
  }

  // ─── Loading / Not Found ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  if (!request) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <FiAlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <h2 className="text-lg font-semibold text-gray-800">Request Not Found</h2>
        <p className="text-sm text-gray-500 mb-4">This supplier payment request does not exist.</p>
        <button onClick={() => navigate("/claims/supplier-payment")} className="px-4 py-2 bg-blue-700 text-white rounded text-sm hover:bg-blue-800">
          Back to List
        </button>
      </div>
    )
  }

  const isTerminal = ["completed", "rejected", "cancelled"].includes(request.status)

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto space-y-5">

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Supplier Payment #{request.id}</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {fullName(request.employee)} · {request.supplier_name}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mt-2">
                <span className="flex items-center gap-1"><FiCalendar size={11} /> {formatDate(request.created_at)}</span>
                <span className="flex items-center gap-1"><FiUser size={11} /> {reasonLabels[request.reason_for_request] || request.reason_for_request}</span>
                <span className="font-mono font-semibold text-gray-700">{fmt(request.total_payable_amount)}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={request.status} />
              {request.status === "requested" && canApprove && (
                <>
                  <button onClick={() => setShowApprove(true)} disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">
                    <FiCheck className="w-4 h-4" /> Approve
                  </button>
                  <button onClick={() => setShowReject(true)} disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50">
                    <FiX className="w-4 h-4" /> Reject
                  </button>
                </>
              )}
              {request.status === "approved" && canTransfer && (
                <button onClick={() => setShowTransfer(true)} disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700 disabled:opacity-50">
                  <FaMoneyBillWave className="w-4 h-4" /> Transfer Payment
                </button>
              )}
              {request.status === "payment" && canApprove && (
                <button onClick={() => setShowComplete(true)} disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50">
                  <FiCheckCircle className="w-4 h-4" /> Mark Complete
                </button>
              )}
              {!isTerminal && (canManage || request.employee_id === user?.id) && (
                <button onClick={() => setShowCancel(true)} disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 disabled:opacity-50">
                  <FaBan className="w-4 h-4" /> Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded shadow-sm">
          <nav className="flex overflow-x-auto border-b border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon as any
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon size={15} /> {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {activeTab === "details" && renderDetails()}
          {activeTab === "payment_info" && canViewPaymentInfo && renderPaymentInfo()}
          {activeTab === "documents" && renderDocuments()}
          {activeTab === "workflow" && renderWorkflow()}
          {activeTab === "logs" && <EntityLogs entity="supplier_payment" entityId={id as string} />}
        </div>
      </div>

      {/* Approve Drawer */}
      <Drawer open={showApprove} title="Approve Request" subtitle="Set the approved amount and optional remarks"
        onClose={() => { setShowApprove(false); setApproveFiles([]) }} disabled={actionLoading}
        footer={
          <>
            <button onClick={() => { setShowApprove(false); setApproveFiles([]) }} disabled={actionLoading}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleApprove} disabled={actionLoading || !approvedAmount}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
              {actionLoading ? "Approving..." : "Approve"}
            </button>
          </>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Approved Amount *</label>
          <input type="number" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)}
            max={request.total_payable_amount} min={0}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500" />
          <p className="text-xs text-gray-400 mt-1">Requested: {fmt(request.total_payable_amount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
          <textarea value={approverRemarks} onChange={(e) => setApproverRemarks(e.target.value)}
            rows={3} placeholder="Add any approval remarks..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500" />
        </div>
        <FileUploadSection files={approveFiles} onChange={setApproveFiles} />
        <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-800">
          The approved amount can be equal to or less than the requested amount.
        </div>
      </Drawer>

      {/* Reject Drawer */}
      <Drawer open={showReject} title="Reject Request" subtitle="Provide a reason for rejection"
        onClose={() => setShowReject(false)} disabled={actionLoading}
        footer={
          <>
            <button onClick={() => setShowReject(false)} disabled={actionLoading}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleReject} disabled={actionLoading || !rejectionRemarks.trim()}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
              {actionLoading ? "Rejecting..." : "Reject"}
            </button>
          </>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remarks *</label>
          <textarea value={rejectionRemarks} onChange={(e) => setRejectionRemarks(e.target.value)}
            rows={4} placeholder="Provide a clear and specific reason..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-red-500" />
        </div>
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800">
          Rejection remarks are required and this action cannot be undone.
        </div>
      </Drawer>

      {/* Transfer Drawer */}
      <Drawer open={showTransfer} title="Transfer Payment" subtitle="Confirm the payment transfer details"
        onClose={() => { setShowTransfer(false); setTransferFiles([]) }} disabled={actionLoading}
        footer={
          <>
            <button onClick={() => { setShowTransfer(false); setTransferFiles([]) }} disabled={actionLoading}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleTransfer} disabled={actionLoading || !transferDate}
              className="px-4 py-2 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50">
              {actionLoading ? "Transferring..." : "Confirm Transfer"}
            </button>
          </>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Transferred Amount</label>
          <input type="number" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)}
            placeholder="Enter amount transferred"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />
          <p className="text-xs text-gray-400 mt-1">Approved: {fmt(request.approved_amount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Date *</label>
          <input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Remarks (optional)</label>
          <textarea value={transferRemarks} onChange={(e) => setTransferRemarks(e.target.value)}
            rows={2} placeholder="Add transfer remarks..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />
        </div>
        <FileUploadSection files={transferFiles} onChange={setTransferFiles} />
      </Drawer>

      {/* Complete Drawer */}
      <Drawer open={showComplete} title="Mark as Complete" subtitle="Confirm that this payment request is complete"
        onClose={() => { setShowComplete(false); setCompleteFiles([]) }} disabled={actionLoading}
        footer={
          <>
            <button onClick={() => { setShowComplete(false); setCompleteFiles([]) }} disabled={actionLoading}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleComplete} disabled={actionLoading}
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
              {actionLoading ? "Completing..." : "Mark Complete"}
            </button>
          </>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Completion Remarks (optional)</label>
          <textarea value={completionRemarks} onChange={(e) => setCompletionRemarks(e.target.value)}
            rows={3} placeholder="Add any completion remarks..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-emerald-500" />
        </div>
        <FileUploadSection files={completeFiles} onChange={setCompleteFiles} />
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800">
          This will mark the payment request as fully completed.
        </div>
      </Drawer>

      {/* Cancel Drawer */}
      <Drawer open={showCancel} title="Cancel Request" subtitle="Provide a reason for cancellation"
        onClose={() => setShowCancel(false)} disabled={actionLoading}
        footer={
          <>
            <button onClick={() => setShowCancel(false)} disabled={actionLoading}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Back
            </button>
            <button onClick={handleCancel} disabled={actionLoading}
              className="px-4 py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50">
              {actionLoading ? "Cancelling..." : "Cancel Request"}
            </button>
          </>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Remarks (optional)</label>
          <textarea value={cancellationRemarks} onChange={(e) => setCancellationRemarks(e.target.value)}
            rows={3} placeholder="Add reason for cancellation..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-gray-500" />
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-700">
          Cancellation cannot be undone. The request will be marked as cancelled.
        </div>
      </Drawer>
    </div>
  )
}

// ─── Tiny helper components ───────────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="text-xs font-medium text-gray-500">{label}</label>
    <div className="text-sm text-gray-900 mt-1">{children}</div>
  </div>
)

const Detail: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <p className="text-xs text-gray-600">
    <span className="font-medium">{k}:</span> {children}
  </p>
)

const FileUploadSection: React.FC<{ files: File[]; onChange: (files: File[]) => void }> = ({ files, onChange }) => {
  const [inputKey, setInputKey] = React.useState(0)
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Documents (optional)</label>
      <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-gray-400 hover:text-blue-600">
        <FiUpload className="w-4 h-4 flex-shrink-0" />
        <span>Click to attach files</span>
        <input
          key={inputKey}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files || [])
            if (picked.length) {
              onChange([...files, ...picked])
              setInputKey(k => k + 1)
            }
          }}
        />
      </label>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between bg-gray-50 px-3 py-1.5 rounded text-xs border border-gray-100">
              <span className="truncate text-gray-700">{f.name}</span>
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0">
                <FiX className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default SupplierPaymentView
